import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import { PanAppState } from '@panamax/app-state';
import { MessageHandlerService } from '@shared/services/message/message-handler.service';
import { ORDER_EXCEPTION_ACTIONS, OrderStatus } from '@usf/ngrx-order';
import { of, combineLatest } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { MessageTypeEnum } from 'src/app/ngrx-message/constants/messageTypeEnum';
import { Message } from 'src/app/ngrx-message/models/client/message';
import { upsertMessage } from 'src/app/ngrx-message/store/actions/message/message.action';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class ClientOrderExceptionEffects {
  resolveOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderSuccess),
      map(action => action.order),
      switchMap(order =>
        combineLatest([
          of(order),
          this.panamax.customer$,
          this.orderExceptionService.isConfirmationFlow(),
        ]).pipe(take(1)),
      ),
      switchMap(([order, customer, isConfirmationFlow]) => {
        const actions: any[] = [];
        if (!order.orderHeader || !!order.orderHeader.errorDetails) {
          actions.push(
            upsertMessage({
              message: {
                id: this.uuid(),
                stack: null,
                watermark: new Date().toUTCString(),
                read: false,
                type: MessageTypeEnum.warning,
                display:
                  'The order has been edited or submitted by another user, please review the order.',
                dismissRoute: ['my-orders-view'],
              } as Message,
            }),
          );
        } else {
          let orderId;
          if (isConfirmationFlow && order.orderHeader.origSplitOrderId) {
            this.orderExceptionService.setIsConfirmationFlow(false);
            orderId = `${customer.customerNumber}:${customer.departmentNumber}:${order.orderHeader.origSplitOrderId}`;
          } else {
            sessionStorage.removeItem('submitOrderExpectedShipments');
            orderId = `${customer.customerNumber}:${customer.departmentNumber}:${order.orderHeader.orderId}`;
          }
          const path = this.getCurrentPath();
          const routeElements = [
            path.split('/')[1],
            'order',
            'order-confirmation',
            orderId,
          ];
          this.routeTo(routeElements);
        }
        return actions;
      }),
    ),
  );

  resolveOrderFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderFail),
      switchMap(error => {
        const actions: any[] = [];
        actions.push(
          upsertMessage({
            message: {
              id: this.uuid(),
              stack: null,
              watermark: new Date().toUTCString(),
              read: false,
              type: MessageTypeEnum.warning,
              display:
                'The order has been edited or submitted by another user, please review the order.',
              dismissRoute: ['my-orders-view'],
            } as Message,
          }),
        );
        return actions;
      }),
    ),
  );

  clientHomePageRouteResolveExceptionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_EXCEPTION_ACTIONS.homePageRouteResolveExceptionsSuccess),
        tap(({ order }) => {
          this.messageHandlerService.dismissSpinnerModal();
          const fullOrderId =
            `${order.orderHeader?.customerNumber}:` +
            `${order.orderHeader?.departmentNumber}:` +
            `${order.orderHeader?.orderId}`;
          this.routeTo(['order', 'order-exceptions', fullOrderId]);
        }),
      ),
    { dispatch: false },
  );

  clientHomePageResovleExceptionFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.homePageRouteResolveExceptionsFail),
      tap(() =>
        setTimeout(() => this.messageHandlerService.dismissSpinnerModal(), 250),
      ),
      switchMap(() => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.warning,
            title: 'Try Again',
            toast: false,
            dismissRoute: ['my-orders-view'],
            display: this.translateService.instant(
              'i18n.home.recentOrdersTile.resolveError',
            ),
          } as Message,
        }),
      ]),
    ),
  );

  clientHomePageResovleExceptionApiFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.homePageRouteResolveExceptionsApiFail),
      tap(() =>
        setTimeout(() => this.messageHandlerService.dismissSpinnerModal(), 250),
      ),
      switchMap(() => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            toast: false,
            type: MessageTypeEnum.warning,
            title: 'Try Again',
            display: this.translateService.instant(
              'i18n.home.recentOrdersTile.resolveApiError',
            ),
          } as Message,
        }),
      ]),
    ),
  );
  getCurrentPath(): string {
    return window.location.pathname;
  }

  routeTo(routeElements: string[]) {
    this.router.navigate([...routeElements]);
  }

  uuid() {
    return uuidv4();
  }

  constructor(
    private actions$: Actions,
    private panamax: PanAppState,
    private router: Router,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private orderExceptionService: OrderExceptionService,
  ) {}
}
